/**
 * The i18n runtime.
 *
 * Two entry points on purpose:
 *
 *   useT()        for components. Re-renders the tree when the locale changes.
 *   tr()          for everything that is not a component — the API client, the
 *                 formatters, `buildRallyPayload`, `partialTotalsCaveat`. These
 *                 produce operator-facing strings but have no React context to
 *                 read from, and threading a dictionary through every one of
 *                 them would just be a hook with extra steps.
 *
 * `activeLocale` is module state written only by `I18nProvider`. It is set from
 * storage at module load so `tr()` is correct before the first render, not one
 * paint later.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { en } from './en';
import { ko } from './ko';
import type { Dictionary } from './dictionary';
import { readStoredLocale, storeLocale, type Locale } from './locale';

export type { Locale } from './locale';
export type { Dictionary } from './dictionary';
export { LOCALES, DEFAULT_LOCALE } from './locale';

const DICTIONARIES: Record<Locale, Dictionary> = { en, ko };

let activeLocale: Locale = readStoredLocale();

export function getLocale(): Locale {
  return activeLocale;
}

/** The dictionary for the active locale, outside React. */
export function tr(): Dictionary {
  return DICTIONARIES[activeLocale];
}

/**
 * Switches locale for non-React code and tests.
 *
 * `I18nProvider` calls this as part of its state update; nothing else in the
 * app should, or the module state and the React state drift apart.
 */
export function setActiveLocale(locale: Locale): void {
  activeLocale = locale;
}

/** The tag handed to `Intl` and to `toLocaleString`. */
export function intlLocale(): string {
  return DICTIONARIES[activeLocale].meta.intlLocale;
}

type I18nContextValue = {
  t: Dictionary;
  locale: Locale;
  setLocale: (locale: Locale) => void;
};

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const initial = readStoredLocale();
    setActiveLocale(initial);
    return initial;
  });

  const setLocale = useCallback((next: Locale) => {
    // Module state first: a formatter called during the render this triggers
    // must already be on the new locale, or the page renders half-translated
    // for one frame.
    setActiveLocale(next);
    storeLocale(next);
    setLocaleState(next);
  }, []);

  useEffect(() => {
    document.documentElement.lang = DICTIONARIES[locale].meta.htmlLang;
  }, [locale]);

  const value = useMemo<I18nContextValue>(
    () => ({ t: DICTIONARIES[locale], locale, setLocale }),
    [locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useT must be used inside <I18nProvider>.');
  return context;
}

/** The dictionary, in a component. Property access, so a typo will not build. */
export function useT(): Dictionary {
  return useI18n().t;
}

export function useLocale(): { locale: Locale; setLocale: (locale: Locale) => void } {
  const { locale, setLocale } = useI18n();
  return { locale, setLocale };
}

/**
 * A server enum rendered in the operator's language.
 *
 * Enum members are an open set — the API can add one before this app knows
 * about it. An unrecognised member falls through to `fallback`, which humanizes
 * the raw id rather than inventing a translation for something we cannot name.
 * That is the same rule the app applies to an unrecognised hero id.
 */
export function translateEnum(
  table: Record<string, string>,
  value: string,
  fallback: (value: string) => string,
): string {
  return table[value] ?? fallback(value);
}
